import { useState } from 'react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { EligibilityGroupCard, ProcessStepCard } from '@/components/Cards';
import { eligibilityGroups, conditions, processSteps, requiredDocs, faqs } from '@/data/index';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Home, DollarSign, MapPin, FileText, ArrowRight } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { Link } from 'react-router-dom';

export default function Eligibility() {
  const [checkedDocs, setCheckedDocs] = useState<Record<string, boolean>>({});

  const toggleDoc = (docId: string) => {
    setCheckedDocs(prev => ({ ...prev, [docId]: !prev[docId] }));
  };

  const conditionIcons = {
    'condition-1': Home,
    'condition-2': DollarSign,
    'condition-3': MapPin,
  };

  return (
    <Layout>
      <div className="min-h-screen">
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-b from-primary/10 via-background to-background py-16 md:py-24"
        >
          <div className="container mx-auto px-4 max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-center mb-8"
            >
              <Badge className="mb-4" variant="secondary">
                Thông tin đăng ký
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Đối Tượng & Điều Kiện Đăng Ký
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Tìm hiểu chi tiết về 9 nhóm đối tượng theo Điều 76 Luật Nhà ở 2023, 3 điều kiện bắt buộc, hồ sơ cần chuẩn bị và quy trình 4 bước mua NOXH HARMONY Xuân Phương
              </p>
            </motion.div>
          </div>
        </motion.section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-7xl">
            <Tabs defaultValue="groups" className="w-full">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8">
                <TabsTrigger value="groups">Đối Tượng Ưu Tiên</TabsTrigger>
                <TabsTrigger value="conditions">Điều Kiện Bắt Buộc</TabsTrigger>
                <TabsTrigger value="documents">Hồ Sơ Cần Chuẩn Bị</TabsTrigger>
                <TabsTrigger value="process">Quy Trình Đăng Ký</TabsTrigger>
              </TabsList>

              <TabsContent value="groups">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold mb-4">9 Nhóm Đối Tượng Theo Điều 76 Luật Nhà ở 2023</h2>
                    <p className="text-muted-foreground">
                      Theo Điều 76 Luật Nhà ở 2023, các nhóm đối tượng sau được ưu tiên mua/thuê mua nhà ở xã hội HARMONY Xuân Phương:
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {eligibilityGroups.map((group, index) => (
                      <motion.div
                        key={group.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.5 }}
                      >
                        <EligibilityGroupCard group={group} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </TabsContent>

              <TabsContent value="conditions">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold mb-4">3 Điều Kiện Mua NOXH HARMONY Xuân Phương</h2>
                    <p className="text-muted-foreground">
                      Người mua nhà ở xã hội HARMONY Xuân Phương phải đáp ứng đầy đủ 3 điều kiện sau:
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {conditions.map((condition, index) => {
                      const Icon = conditionIcons[condition.id as keyof typeof conditionIcons];
                      return (
                        <motion.div
                          key={condition.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1, duration: 0.5 }}
                        >
                          <Card className="h-full hover:shadow-lg transition-shadow">
                            <CardHeader>
                              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                                <Icon className="w-6 h-6 text-primary" />
                              </div>
                              <CardTitle className="text-xl">{condition.title}</CardTitle>
                              <CardDescription>{condition.description}</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ul className="space-y-3">
                                {condition.requirements.map((req, idx) => (
                                  <li key={idx} className="flex items-start gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                    <span className="text-sm">{req}</span>
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className="mt-8 p-6 bg-accent/20 rounded-lg border border-accent"
                  >
                    <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-accent-foreground" />
                      Lưu ý quan trọng
                    </h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• <strong>Đối tượng ưu tiên</strong>: Lực lượng vũ trang CAND (Khoản 7 Điều 76) được ưu tiên xét duyệt đầu tiên</li>
                      <li>• <strong>Thu nhập LLVTND</strong>: Mức thu nhập tối đa tính theo lương Đại tá – xác minh qua đơn vị công tác</li>
                      <li>• <strong>Điều kiện nhà ở</strong>: Xác minh tại tỉnh Bắc Giang – không có tên trong GCN QSDD tại thời điểm nộp hồ sơ</li>
                      <li>• <strong>Nộp hồ sơ</strong>: Từ Quý I/2026 tại Tầng 5, CT1 The Pride Tố Hữu, Hà Đông, Hà Nội</li>
                      <li>• <strong>Tải biểu mẫu</strong>: Quét mã QR tại văn phòng dự án hoặc gọi hotline 0917978466</li>
                    </ul>
                  </motion.div>
                </motion.div>
              </TabsContent>

              <TabsContent value="documents">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold mb-4">Hồ Sơ Cần Chuẩn Bị</h2>
                    <p className="text-muted-foreground">
                      Danh sách hồ sơ cần thiết để đăng ký mua/thuê mua nhà ở xã hội (theo quy định 2026):
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {requiredDocs.map((doc, index) => (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.5 }}
                      >
                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                              <Checkbox
                                id={doc.id}
                                checked={checkedDocs[doc.id] || false}
                                onCheckedChange={() => toggleDoc(doc.id)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <label
                                  htmlFor={doc.id}
                                  className="font-semibold cursor-pointer flex items-center gap-2"
                                >
                                  {doc.name}
                                  {doc.required && (
                                    <Badge variant="destructive" className="text-xs">
                                      Bắt buộc
                                    </Badge>
                                  )}
                                </label>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {doc.description}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="mt-8 p-6 bg-primary/5 rounded-lg border border-primary/20"
                  >
                    <h3 className="text-lg font-semibold mb-3">Hướng dẫn chuẩn bị hồ sơ</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Tất cả giấy tờ cần bản sao có công chứng hoặc xác thực qua VNeID</li>
                      <li>• Giấy xác nhận thu nhập phải là bảng lương 12 tháng gần nhất</li>
                      <li>• Giấy tờ chứng minh đối tượng tùy thuộc vào nhóm bạn thuộc (xem tab Đối Tượng Ưu Tiên)</li>
                      <li>• Có thể nộp hồ sơ trực tiếp tại văn phòng chủ đầu tư hoặc qua cổng dịch vụ công trực tuyến</li>
                    </ul>
                  </motion.div>
                </motion.div>
              </TabsContent>

              <TabsContent value="process">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold mb-4">Quy Trình Đăng Ký 7 Bước</h2>
                    <p className="text-muted-foreground">
                      Quy trình đăng ký mua/thuê mua nhà ở xã hội được thực hiện qua 7 bước sau:
                    </p>
                  </div>
                  <div className="relative">
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden md:block" />
                    <div className="space-y-8">
                      {processSteps.map((step, index) => (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1, duration: 0.5 }}
                        >
                          <ProcessStepCard step={step} />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                    className="mt-12 p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20"
                  >
                    <h3 className="text-lg font-semibold mb-3">Tổng thời gian dự kiến</h3>
                    <p className="text-muted-foreground">
                      Từ khi bắt đầu chuẩn bị hồ sơ đến khi nhận kết quả xét duyệt: khoảng <span className="font-semibold text-foreground">12-15 ngày</span>. Thời gian bàn giao căn hộ phụ thuộc vào tiến độ xây dựng của dự án.
                    </p>
                  </motion.div>
                </motion.div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-8 text-center">Câu Hỏi Thường Gặp</h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq) => (
                  <AccordionItem key={faq.id} value={faq.id}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 md:p-12 text-center text-primary-foreground"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Sẵn Sàng Đăng Ký?
              </h2>
              <p className="text-lg mb-8 opacity-90">
                Kiểm tra điều kiện của bạn và bắt đầu chuẩn bị hồ sơ ngay hôm nay
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  variant="secondary"
                  className="text-lg"
                  asChild
                >
                  <Link to={ROUTE_PATHS.ANNOUNCEMENT}>
                    Xem Thông Báo Mở Bán
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                  asChild
                >
                  <Link to={ROUTE_PATHS.LEGAL}>Xem Hồ Sơ Pháp Lý</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}